import {
  Button,
  CondensedButton,
  Flex,
  Heading,
  IconDownloadLine,
  IconSaveLine,
  IconUploadLine,
  InlineList,
  Spinner,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import RichTextEditor from "react-rte";
import { ExternalService } from "../../../../../services/ExternalService";
import downloadFile from "../../../../../utils/Dom";
import { fileWriter } from "../../../../../utils/FileUtils";
import {
  ERROR,
  INFO,
  useNotification,
} from "../../../../CustomHooks/NotificationProvider";
import CustomRTE, { EMPTY_RTE } from "../../../../CustomTextArea/CustomRTE";
import UploadMeasuresModal from "../../../../CustomTextArea/UploadMeasuresModal";

const QualitativeMeasure = ({ measure, permission }) => {
  const showNotification = useNotification();
  const [evidence, setEvidence] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const getFileContent = async () => {
      try {
        const filePromises = [
          ExternalService.getFileContent(
            "response.html",
            "qualitativeEvidence",
            measure.ddl_id
          ),
          ExternalService.getFileContent(
            "response.html",
            "qualitativeMeasure",
            measure.ddl_id
          ),
        ];

        const [evidence, measureInstructions] = await Promise.all(filePromises);

        if (evidence.content === "" && !permission) {
          setEvidence(
            "<p><em>No measure results or evidence have been submitted yet.</em></p>"
          );
        } else {
          setEvidence(evidence.content);
        }
        setInstructions(measureInstructions.content);
      } catch {
        showNotification(
          t("notifications.assessmentResults.getMeasureError"),
          ERROR
        );
      }
    };
    getFileContent();
  }, [measure, permission, showNotification]);

  const handleSaveEvidence = async (id, content) => {
    setSaving(true);
    try {
      const measure = fileWriter(content, "response.html", "text/html");
      await ExternalService.saveFile(measure, "qualitativeEvidence", id);
      showNotification(t("notifications.assessmentResults.addMeasureSuccess"));
    } catch {
      showNotification(
        t("notifications.assessmentResults.addMeasureError"),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadMeasureEvidence = async () => {
    setDownloading(true);
    try {
      let paths = await ExternalService.reviewFiles(
        "qualitativeEvidence",
        measure.ddl_id
      );
      paths = paths.filter((file) => file.file_name !== "response.html");
      if (paths.length > 0) {
        paths.forEach((element) => {
          downloadFile(element.file_url, element.file_name);
        });
      } else {
        showNotification(
          t("results.noEvidenceForMeasure", { measureName: measure.name }),
          INFO
        );
      }
    } catch {
      showNotification(
        t("notifications.assessmentResults.downloadMeasureEvidenceError"),
        ERROR
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{measure.name}</Heading>
      </Flex.Item>
      {evidence !== null && instructions !== null ? (
        <>
          {!!instructions && instructions !== EMPTY_RTE && (
            <Flex.Item size="100%" padding="0 0 0 large">
              <RichTextEditor
                className="readOnlyClass"
                readOnly
                value={RichTextEditor.createValueFromString(
                  instructions,
                  "html"
                )}
              />
            </Flex.Item>
          )}
          <Flex.Item size="100%" padding="0 0 0 large">
            <CustomRTE
              id={measure.ddl_id}
              label={
                <Flex wrap="wrap" gap="small">
                  <Flex.Item>
                    <Heading level="h4">{t("results.measureEvidence")}</Heading>
                  </Flex.Item>
                  <Flex.Item>
                    <Text fontStyle="italic">
                      {t("measures.assignedTo")}: {measure.assignees}
                    </Text>
                  </Flex.Item>
                </Flex>
              }
              defaultValue={evidence}
              onChange={(value) => setEvidence(value)}
              readOnly={!permission}
            />
          </Flex.Item>
          {measure.upload_evidence && (
            <Flex.Item shouldGrow textAlign="end">
              <InlineList delimiter="pipe">
                {permission && (
                  <InlineList.Item>
                    <CondensedButton
                      renderIcon={IconUploadLine}
                      onClick={() => setUploadDialog(true)}
                    >
                      {t(
                        "notifications.assessmentResults.uploadMeasureEvidence"
                      )}
                    </CondensedButton>
                  </InlineList.Item>
                )}
                <InlineList.Item>
                  <CondensedButton
                    renderIcon={
                      downloading ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconDownloadLine
                      )
                    }
                    disabled={downloading}
                    onClick={downloadMeasureEvidence}
                  >
                    {t(
                      "notifications.assessmentResults.downloadMeasureEvidence"
                    )}
                  </CondensedButton>
                </InlineList.Item>
              </InlineList>
            </Flex.Item>
          )}
          {permission && (
            <Flex.Item shouldGrow={!measure.upload_evidence} textAlign="end">
              <Button
                color="primary"
                size="small"
                onClick={() => handleSaveEvidence(measure.ddl_id, evidence)}
                disabled={saving}
                renderIcon={
                  saving ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    IconSaveLine
                  )
                }
              >
                {t("results.saveMeasureEvidence")}
              </Button>
            </Flex.Item>
          )}
          <UploadMeasuresModal
            id={measure.ddl_id}
            uploadDialog={uploadDialog}
            setUploadDialog={setUploadDialog}
          />
        </>
      ) : (
        <Flex.Item shouldGrow>
          <Skeleton height="10rem" count={2} style={{ marginBottom: 10 }} />
        </Flex.Item>
      )}
    </Flex>
  );
};

export default QualitativeMeasure;
